// Push приложения в окне «Уведомления»: есть ли что предложить, и что сейчас с этим
// телефоном. Сама работа с плагином и сервером — в `shared/push`, подключение при входе
// и после смены выбора — в `pushSync.ts`.

import { useMutation } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';

import { currentPushHere, pushSupported, subscribePushHere, type PushHere } from '@/shared/push';

import type { PushAlertChannel } from './alertSettingsTypes';
import { isLegacy, useAlertSettings, useTelegramConnection } from './api';
import { useAlertFeedAvailable } from './feed/api/useAlertFeed';
import { allowFromDialog } from './pushSync';

/**
 * Раздел push есть в окне: ключ Firebase на сервере — и либо эта установка умеет push,
 * либо выбор человека включён. На планшете Windows и в браузере push не приходит,
 * но включённый человеком виден и там: оттуда его можно выключить или сменить пороги.
 */
export function pushOffered(push: PushAlertChannel, seenEnabled = false): boolean {
  return push.available && (pushSupported() || push.enabled || seenEnabled);
}

/**
 * Есть что предложить в окне «Уведомления»: бот Telegram, push — или оба.
 *
 * По alert-settings. Пока идёт перезапрос или сеть упала, TanStack Query держит
 * последний ответ, и пункт меню не пропадает: пропадающий пункт на кухне без сети
 * выглядел бы тем же «слетело» (B-039). Прежний сервер (404) — только Telegram:
 * push там этот бандл не предлагает.
 */
export function useNotificationsAvailable(): boolean {
  const settings = useAlertSettings().data;
  // Есть лента точки — окно настроек есть всегда: в нём пороги точки и галочка Windows (T-047).
  const feed = useAlertFeedAvailable();
  const legacy = settings !== undefined && isLegacy(settings);
  const telegram = useTelegramConnection({ enabled: legacy }).data?.available ?? false;
  if (feed) return true;
  if (settings === undefined) return false;
  if (isLegacy(settings)) return telegram;
  return settings.telegram.available || pushOffered(settings.push);
}

/** Что сейчас с push на ЭТОМ телефоне — строка под галочкой окна. `null` — ещё не подключали. */
export function usePhoneStatus(): PushHere | null {
  return useSyncExternalStore(subscribePushHere, currentPushHere, () => null);
}

/** «Разрешить уведомления»: повторный системный запрос и подключение этого телефона. */
export function useAllowNotifications() {
  return useMutation({ mutationFn: allowFromDialog });
}
